import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  CalendarDays,
  History,
  Info,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import PageHeader from "@/components/shared/PageHeader";
import DataExportButton from "@/components/shared/DataExportButton";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuthStore } from "@/store/authStore";
import { useAuditLogs } from "@/hooks/useAuditLogs";

export const Route = createFileRoute("/admin/audit-logs/")({
  component: AdminAuditLogsPage,
});

function AdminAuditLogsPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const { user } = useAuthStore();
  const { data: logs, isLoading } = useAuditLogs();

  const filteredLogs = (logs || []).filter((log) => {
    const matchesSearch =
      String(log.user_id).includes(search) ||
      log.action.toLowerCase().includes(search.toLowerCase()) ||
      log.target_type?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || log.target_type === typeFilter;
    // Severity isn't directly in the model yet, we'll infer it or use 'info'
    const severity =
      log.action.toLowerCase().includes("fail") || log.action.toLowerCase().includes("reject")
        ? "error"
        : log.action.toLowerCase().includes("update")
          ? "warning"
          : "info";
    const matchesSeverity = severityFilter === "all" || severity === severityFilter;
    return matchesSearch && matchesType && matchesSeverity;
  });

  const getSeverityStyles = (
    severity: string,
  ): { variant: NonNullable<BadgeProps["variant"]>; icon: React.ReactNode } => {
    switch (severity) {
      case "success":
        return { variant: "success", icon: <ShieldCheck /> };
      case "warning":
        return { variant: "warning", icon: <TriangleAlert /> };
      case "error":
        return { variant: "destructive", icon: <ShieldAlert /> };
      default:
        return { variant: "muted", icon: <Info /> };
    }
  };

  const getSeverity = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes("fail") || a.includes("reject") || a.includes("error")) return "error";
    if (a.includes("update") || a.includes("change") || a.includes("edit")) return "warning";
    if (a.includes("approve") || a.includes("create") || a.includes("success")) return "success";
    return "info";
  };

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="System forensic logs"
        subtitle="Transparent immutable ledger of all platform operations, security events, and administrative actions."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Audit Logs" }]}
        action={
          <DataExportButton
            onExport={(format) => console.log("Exporting logs as", format)}
            label="Download audit trail"
          />
        }
      />

      {/* Analytics Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total log entries" value={String(logs?.length || 0)} icon={<History />} />
        <StatCard
          label="Critical security flags"
          value={String(logs?.filter((l) => getSeverity(l.action) === "error").length || 0)}
          icon={<ShieldAlert />}
        />
        <StatCard
          label="Latest activity"
          value={
            logs && logs.length > 0 ? new Date(logs[0].created_at).toLocaleDateString() : "N/A"
          }
          icon={<CalendarDays />}
        />
      </div>

      {/* Main Ledger Section */}
      <SectionCard
        title="Event ledger"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search logs…"
              className="min-w-0 flex-1 sm:w-60 sm:flex-none"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[140px]" aria-label="Target type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="user">User actions</SelectItem>
                <SelectItem value="payment">Financials</SelectItem>
                <SelectItem value="security">Security</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {filteredLogs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Event timestamp</TableHead>
                <TableHead>Originator (ID)</TableHead>
                <TableHead>Action performed</TableHead>
                <TableHead>Target type</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">IP address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => {
                const severity = getSeverity(log.action);
                const style = getSeverityStyles(severity);
                return (
                  <TableRow key={log.id} className="h-11 [&>td]:py-1.5">
                    <TableCell className="whitespace-nowrap pl-4 font-mono text-caption text-muted-foreground tabular-nums md:pl-5">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="size-6 rounded-full">
                          <AvatarFallback className="rounded-full text-[10px]">
                            {log.user_id ? "U" : "S"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-mono text-foreground">{log.user_id || "System"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[180px] text-foreground">{log.action}</TableCell>
                    <TableCell className="text-muted-foreground">{log.target_type || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={style.variant} className="capitalize">
                        {style.icon}
                        {severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="pr-4 text-right font-mono text-caption text-muted-foreground md:pr-5">
                      {log.ip_address || "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            bare
            icon={<ScrollText />}
            title="No audit logs found"
            description="Try a different search term or type filter."
          />
        )}
      </SectionCard>
    </div>
  );
}
